import { perfLog } from "lib/perf/perf";
import type { VirtualLayout } from "lib/types";
import {
  clearStore,
  deleteFromDB,
  getFromDB,
  setInDB,
} from "lib/utils/indexed-db";

const STORE_NAME = "layouts";

export function getCacheKey(bookId: number, layoutKey: string): string {
  return `${bookId}:${layoutKey}`;
}

function isLayoutValid(layout: VirtualLayout): boolean {
  const viewportWidth = window.innerWidth;
  const remainder = layout.contentWidth % viewportWidth;
  return remainder < 1 || remainder > viewportWidth - 1;
}

export async function getCachedLayout(
  cacheKey: string
): Promise<VirtualLayout | null> {
  const layout = await getFromDB<VirtualLayout>(STORE_NAME, cacheKey);
  if (!layout) {
    perfLog("cache", "MISS", { key: cacheKey });
    return null;
  }

  if (!isLayoutValid(layout)) {
    perfLog("cache", "INVALID", {
      key: cacheKey,
      contentWidth: layout.contentWidth,
      viewportWidth: window.innerWidth,
    });
    deleteFromDB(STORE_NAME, cacheKey);
    return null;
  }

  perfLog("cache", "HIT", { key: cacheKey });
  return layout;
}

export async function setCachedLayout(
  cacheKey: string,
  layout: VirtualLayout
): Promise<void> {
  if (!isLayoutValid(layout)) {
    perfLog("cache", "SKIP_INVALID", {
      key: cacheKey,
      contentWidth: layout.contentWidth,
      viewportWidth: window.innerWidth,
    });
    return;
  }

  await setInDB(STORE_NAME, cacheKey, layout);
  perfLog("cache", "saved", {
    key: cacheKey,
    totalColumns: layout.totalColumns,
    totalPages: layout.totalPages,
    parts: Object.keys(layout.parts).length,
    elements: Object.keys(layout.elements).length,
  });
}

export async function clearLayoutCache(): Promise<void> {
  await clearStore(STORE_NAME);
  perfLog("cache", "cleared");
}
